import { useEffect, useState } from 'react'

import { Trash } from 'lucide-react'
import { Button, Col, FormGroup, Input, Label, Row } from 'twyne-new.ui'

import { AddButton } from '@Components/ConsentDBAs/Tabs/DBA'
import { useConsentDBAsStore } from '@Components/ConsentDBAs/store'
import { PureSwitch } from '@Components/form/Switch'

export default function TcpaConsentDbaList({ fields, onChange, override, overrideValues, onApply }) {
  const { collections: { useLoad, items: dbas }, collection: { save } } = useConsentDBAsStore()
  const [overrideSwitch, setOverrideSwitch] = useState()
  const [options, setOptions] = useState([])
  const [errors, setErrors] = useState([])

  const loadDBAs = useLoad()

  useEffect(() => {
    if (dbas?.length) {
      const options = dbas.map(({ label, value }) => ({ label, value }))
      setOptions(options)
    }
  }, [dbas])

  useEffect(() => {
    if (overrideValues) {
      setOverrideSwitch(true)
    }
  }, [overrideValues])

  fields = fields || []

  const onAdd = option => {
    const { label, value } = option
    fields.push({ label, value })
    onChange([...fields])
  }

  const onRemove = fieldPosition => {
    fields.splice(fieldPosition, 1)
    onChange([...fields])
    onRemoveError(fieldPosition)
  }

  const handleChange = (fieldPosition, fieldLabel, fieldValue) => {
    fields[fieldPosition][fieldLabel] = fieldValue
    onChange(fields)
  }

  const handleChangeOverrideSwitch = check => {
    setOverrideSwitch(check)
    onChange(check ? fields : undefined)
  }

  const onAddError = idx => {
    if (!errors.includes(idx)) {
      setErrors([...errors, idx])
    }
  }

  const onRemoveError = idx => {
    setErrors(errors.filter(error => error !== idx))
  }

  const handleSave = async dba => {
    if (dba.isNew) {
      await save({ dba })
      await loadDBAs()
    }
  }

  return <>
    <Row>
      <Col size='12'>
        <FormGroup className="m-0">
          <div className="flex items-center justify-between my-2">
            <p className='my-3'>These data will be used to filter DBAs in the integration rules</p>
          </div>
        </FormGroup>
        {override && <>
          <div className='flex justify-between'>
            <FormGroup>
              <Label>Override Consent DBA</Label>
              <PureSwitch
                onChange={e => handleChangeOverrideSwitch(e.target.checked)}
                value={overrideSwitch} />
              <em />
            </FormGroup>
          </div>
        </>}
      </Col>
      <Col size='12'>
        {fields && fields.length ? <>
          <Row>
            <Col size='5'>
              <Label>Name</Label>
            </Col>
            <Col size='5'>
              <Label>Value</Label>
            </Col>
            <Col size='2'></Col>
          </Row>
          {fields.map(({ label, value }, idx) => <>
            <TcpaConsentDbaListItem
              key={idx}
              idx={idx}
              label={label}
              value={value}
              handleChange={(field, value) => handleChange(idx, field, value)}
              handleRemove={() => onRemove(idx)}
              disabled={override && !overrideSwitch}
              handleAddError={() => onAddError(idx)}
              handleRemoveError={() => onRemoveError(idx)}
            />
          </>)}
        </> : <>
          <div className='flex justify-center'>
            <Label>No DBA's found</Label>
          </div>
        </>}
      </Col>
    </Row>

    <div className='flex gap-2 justify-end'>
      <Button color="accent" onClick={onApply} disabled={errors.length}>Apply</Button>
      <AddButton color="success" options={options} onAdd={onAdd} onSave={handleSave} disabled={override && !overrideSwitch} />
    </div>
  </>
}

const TcpaConsentDbaListItem = ({ idx, label, value, handleChange, handleRemove, disabled, handleAddError, handleRemoveError }) => {
  const [valueValid, setValueValid] = useState(true)

  const isValid = value => {
    const regex = /^[a-zA-Z0-9]*$/
    const regexTestValid = regex.test(value)
    regexTestValid ? handleRemoveError() : handleAddError()
    return regexTestValid
  }

  const handleValueChange = value => {
    setValueValid(isValid(value))
    handleChange('value', value)
  }

  return <Row className='my-2'>
    <Col size='5'>
      <FormGroup>
        <Input
          disabled={disabled}
          name={`fields.${idx}.label`}
          value={label}
          onChange={e => handleChange('label', e.target.value)}
          required
        />
      </FormGroup>
    </Col>
    <Col size='6'>
      <FormGroup>
        <Input
          required
          disabled={disabled}
          name={`fields.${idx}.value`}
          value={value}
          onChange={e => handleValueChange(e.target.value)}
          className={!valueValid ? 'is-invalid' : ''}
        />
        <span className="invalid-feedback">
          Special characters are not allowed.
        </span>
      </FormGroup>
    </Col>
    <Col size='1' className='h-full pb-3'>
      <Button disabled={disabled} className="h-full" color="danger" variant="light" onClick={handleRemove}>
        <Trash size='16' />
      </Button>
    </Col>
  </Row>
}

